import { api } from '@/lib/api';
import type {
  ShiftResponse,
  ShiftQuery,
  ShiftListResponse,
  CreateShiftRequest,
  UpdateShiftRequest,
} from '@troia-v3/types';

export interface ShiftMemberResponse {
  id: string;
  name: string;
  email: string;
  picture?: string;
  avatar?: string;
  shiftRole: 'primary' | 'backup';
  priority?: number;
  currentStatus?: string;
  currentWorkload?: number;
  lastActivity?: string;
  lastAssignment?: string;
}

export interface AddMemberRequest {
  userId: string;
  role: 'primary' | 'backup';
  priority?: number;
}

export const shiftsService = {
  /**
   * Get all shifts with pagination and filters
   */
  async getShifts(query?: ShiftQuery): Promise<ShiftListResponse> {
    const response = await api.get('shifts', { params: query });
    return response.data.data;
  },

  /**
   * Get shift by ID
   */
  async getShiftById(id: string): Promise<ShiftResponse> {
    const response = await api.get(`shifts/${id}`);
    return response.data.data;
  },

  /**
   * Create new shift
   */
  async createShift(data: CreateShiftRequest): Promise<ShiftResponse> {
    const response = await api.post('shifts', data);
    return response.data.data;
  },

  /**
   * Update shift
   */
  async updateShift(id: string, data: UpdateShiftRequest): Promise<ShiftResponse> {
    const response = await api.put(`shifts/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete shift
   */
  async deleteShift(id: string): Promise<void> {
    await api.delete(`shifts/${id}`);
  },

  /**
   * Duplicate shift (clones config; members are not copied)
   */
  async duplicateShift(id: string): Promise<ShiftResponse> {
    const response = await api.post(`shifts/${id}/duplicate`);
    return response.data.data;
  },

  /**
   * Activate shift
   */
  async activateShift(id: string): Promise<ShiftResponse> {
    const response = await api.post(`shifts/${id}/activate`);
    return response.data.data;
  },

  /**
   * Deactivate shift
   */
  async deactivateShift(id: string): Promise<ShiftResponse> {
    const response = await api.post(`shifts/${id}/deactivate`);
    return response.data.data;
  },

  /**
   * Get shifts for dropdown (minimal data for selects/filters)
   */
  async getShiftsForDropdown(): Promise<Array<{ id: string; name: string; description?: string }>> {
    const response = await api.get('shifts/dropdown');
    return response.data.data;
  },

  /**
   * Get shift members
   */
  async getShiftMembers(shiftId: string, role?: 'primary' | 'backup'): Promise<ShiftMemberResponse[]> {
    const params = role ? { role } : {};
    const response = await api.get(`shifts/${shiftId}/members`, { params });
    return response.data.members || response.data.data || [];
  },

  /**
   * Add member to shift
   */
  async addMember(shiftId: string, data: AddMemberRequest): Promise<any> {
    const response = await api.post(`shifts/${shiftId}/members`, data);
    return response.data.data;
  },

  /**
   * Remove member from shift
   */
  async removeMember(shiftId: string, userId: string): Promise<void> {
    await api.delete(`shifts/${shiftId}/members/${userId}`);
  },

  /**
   * Update member role in shift
   */
  async updateMemberRole(shiftId: string, userId: string, role: 'primary' | 'backup'): Promise<void> {
    await api.patch(`shifts/${shiftId}/members/${userId}`, { role });
  },

  /**
   * Get shifts by team
   */
  async getShiftsByTeam(teamId: string): Promise<ShiftResponse[]> {
    const response = await api.get(`shifts/team/${teamId}`);
    return response.data.data || response.data || [];
  },

  /**
   * Get user shifts
   */
  async getUserShifts(userId: string): Promise<ShiftResponse[]> {
    const response = await api.get(`shifts/user/${userId}`);
    return response.data.data || response.data || [];
  },
};
